import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { ColumnConfig } from "@shared/components/Grid/types";
import { global } from "@shared/libs/Global";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import SnMemoBox from "@shared/components/SnMemoBox";
import SnTab from "@shared/components/SnTab";
import SnGrid from "@shared/components/Grid/SnGrid";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnResizable from "@shared/components/SnResizable";
import SnButton from "@shared/components/SnButton";
import { showPopupModal } from "@widgets/CommonModal";
import SN_SFA_ISSUE_REG_004_POP_001 from "@menu/SFA/SN_SFA_ISSUE_REG_004/SN_SFA_ISSUE_REG_004_POP_001";
import { getProgress } from "@shared/utils/IssueUtils";
import { showDialog } from "@widgets/CommonDialog";
import FreeForm from "@widgets/FreeForm";
import SN_SFA_ISSUE_REG_004_POP_002 from "@menu/SFA/SN_SFA_ISSUE_REG_004/SN_SFA_ISSUE_REG_004_POP_002";
import { UseApiService } from "@shared/hooks/useApiService";

interface Props {
  Menuprops: menuInfo;
}

/**
 * SNOTES 스크립트 관리
 */
const SN_SFA_ISSUE_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridRef1 = useRef<any>(null);
  const gridRef2 = useRef<any>(null);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<ColumnConfig[]>([]);
  const [columns2, setColums2] = useState<ColumnConfig[]>([]);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const [dataSource2, setDataSource2] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("1");
  const [gridFocus, setGridFocus] = useState(false);
  const [focusedRowData, setFocusedRowData] = useState<any>();
  const [script, setScript] = useState("");
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_SFA_ISSUE_REG_004";
  const defaultSize = { panel1width: "70%", panel2width: "29%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  const { apiPost } = UseApiService();

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums1(rtn.grid.grid1);
        setColums2(rtn.grid.grid2);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridRef1.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  const onSearch = async () => {
    gridRef1.current?.api.clearCellSelection();
    gridRef1.current?.api.deselectAll();

    try {
      const params = {
        id: "sn_sfa_issue_reg_004_01",
        params: searchRef.current.getFormdata(),
      };

      setDataSource1([]);
      setDataSource2([]);
      const data: any = await fnGetDataSet(params);

      // 조회 데이터 후처리
      const processed = (data[0] || []).map((row: any) => {
        if (row.fg_script === "S") {
          const arr = JSON.parse(row.txt_script || "[]");
          if (Array.isArray(arr)) {
            row.txt_script = arr
              .map(
                (q: any) => `INSERT INTO sys_query (cd_query, dc_query, dc_rmk)
VALUES ('${escapeLiteral(q.cd_query)}', '${escapeLiteral(q.dc_query)}', '${escapeLiteral(q.dc_rmk)}')
ON CONFLICT (cd_query) DO UPDATE
SET dc_query = EXCLUDED.dc_query,
    dc_rmk   = EXCLUDED.dc_rmk;`
              )
              .join("\n\n"); // 구문 사이에 줄바꿈 추가
          }
        }
        return row;
      });

      setDataSource1(processed);

      if (processed.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        setGridFocus(true);
      }

      gridRef1.current.resetUpdate();
      gridRef2.current.resetUpdate();
    } catch (error) {}
  };

  const onFocusedRowChanged = useCallback(
    (focusedRowData: any | null) => {
      setFocusedRowData(focusedRowData);

      const selectedRow = focusedRowData;
      if (selectedRow) {
        setScript(selectedRow.txt_script || "");
      } else {
        setDataSource2([]);
        return;
      }
    },
    [focusedRowData]
  );

  // 문자열 escape 유틸 함수
  function escapeLiteral(str: any): string {
    if (str === null || str === undefined) return "";
    return String(str).replace(/'/g, "''");
  }

  const onTabBtnsClick = async (id: string) => {
    if (id === "btnExtract") {
      const scriptBundle = (dataSource1 || [])
        .map((row: any) => {
          return `-------- 이슈번호 : ${row.no_issue}
-------- 제   목 : ${row.dc_title}
${row.txt_script || ""}
`;
        })
        .join("\n");

      const component = (
        <SN_SFA_ISSUE_REG_004_POP_001 txt_script={scriptBundle} />
      );
      const result = await showPopupModal({
        title: "Script 추출 도움창",
        children: component,
        okayBtn: false,
      });
      if (!result || result === "close") return;
    } else if (id === "btnDeploy") {
      if (!dataSource1 || dataSource1.length === 0) {
        await showDialog("alter", "배포할 스크립트가 존재하지 않습니다.");
        return;
      }

      // MSSQL 스크립트와 그 외 DB 스크립트는 배포 로직이 달라 한 번에 배포할 수 없다. 혼용 시 즉시 중단
      const isMssql = (row: any) =>
        String(row?.tp_sql ?? "")
          .trim()
          .toUpperCase() === "MSSQL";
      const sqlKinds = new Set(dataSource1.map(isMssql));
      if (sqlKinds.size > 1) {
        await showDialog(
          "alter",
          "MSSQL 스크립트와 다른 DB 스크립트가 함께 조회되어 있습니다.\n제품구분을 한 종류로 조회한 뒤 배포하세요."
        );
        return;
      }

      // X연월일시분초FG_SCRIPT
      const applyFlag = dataSource1[0]?.no_version === null;
      if (applyFlag) {
        // 1. no_issue list 추출해서 조회
        const no_issue_list = Array.from(
          new Set(dataSource1.map((row) => row.no_issue))
        ).join(",");

        const params = {
          id: "sn_sfa_issue_reg_004_02",
          params: {
            cd_firm: userInfo.cd_firm,
            no_issue_list: no_issue_list,
          },
        };
        const result = await fnGetDataSet(params);

        // 2. MSSQL이면 배포 서버에 스크립트 배포
        let versions: Record<string, string> | undefined;
        if (dataSource1.every(isMssql)) {
          const items = dataSource1.map((row: any) => ({
            no_issue: row.no_issue,
            fg_script: row.fg_script,
            txt_script: row.txt_script,
          }));
          const rtn = await callApi("executeScript/publishScriptDotNet", {
            items,
          });
          if (!rtn?.status) return;
          versions = {};
          for (const d of rtn.data ?? []) {
            versions[`${d.no_issue}|${d.fg_script}`] = d.no_version;
          }
        }

        // 3. dataSource1이랑 merge해서 payload 만들기
        const payload = buildDeployPayload(result[0], dataSource1, versions);

        // 4) 서버 저장 호출
        try {
          const result = await fnSave(
            "SN_SFA_ISSUE_REP_001_btnNewIssue",
            payload
          );

          if (result) {
            if (!payload) return;

            const updatedGridData = dataSource1.map((row) => {
              const updatedIssue = payload.sfa_issue.update.find(
                (u: any) => u.no_issue === row.no_issue
              );
              if (!updatedIssue) return row;

              const updatedScript = (updatedIssue.js_script || []).find(
                (s: any) => s.fg_script === row.fg_script
              );
              if (updatedScript) {
                return {
                  ...row,
                  yn_apply: updatedScript.yn_apply,
                  tm_apply: updatedScript.tm_apply,
                  no_version: updatedScript.no_version,
                };
              }
              return row;
            });

            setDataSource1(updatedGridData);
            gridRef1.current.api.setRowData(updatedGridData);
          }
        } catch (err) {}
      } else {
        await showDialog("alter", "이미 배포 완료된 스크립트입니다.");
      }
    } else if (id === "btnReject") {
      const selectedRow = gridRef1.current.getSelectedRow();
      const no_issue = selectedRow && selectedRow[0]?.no_issue;

      if (!no_issue) {
        await showDialog("alter", "선택된 스크립트가 없습니다.");
        return;
      }

      if (!selectedRow[0]?.no_version) {
        await showDialog("alter", "아직 배포되지 않은 스크립트입니다.");
        return;
      }

      // 1. 원본 조회
      const params = {
        id: "sn_sfa_issue_reg_004_03",
        params: { cd_firm: userInfo.cd_firm, no_issue },
      };
      const result = await fnGetDataSet(params);
      const origin_progress = result[0];
      const origin_script = result[1];

      // 2. js_progress 초기화
      const js_progress = getProgress({
        ...origin_progress[0],
        ...{ tm_test: null },
      });

      // 3. js_script 초기화
      const js_script = origin_script.map((s: any) => ({
        ...s,
        yn_apply: "N",
        tm_apply: null,
        no_version: null,
      }));

      // 4. payload
      const updateData: any = {
        cd_firm: userInfo.cd_firm,
        no_issue: no_issue,
        cd_user_amd: userInfo.cd_user,
        st_issue: "T",
        js_progress,
        js_script,
      };

      const saveData = { sfa_issue: { update: [updateData] } };

      // 5. 서버 저장
      const saveResult = await fnSave(
        "SN_SFA_ISSUE_REP_001_btnNewIssue",
        saveData
      );

      if (saveResult) {
        const newData = dataSource1.filter((row) => row.no_issue !== no_issue);
        setDataSource1(newData);
      }
    }
  };

  // 날짜 포맷 yyyy-MM-dd HH:mm:ss
  function formatDateTime(date: Date) {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return (
      date.getFullYear() +
      pad(date.getMonth() + 1) +
      pad(date.getDate()) +
      pad(date.getHours()) +
      pad(date.getMinutes()) +
      pad(date.getSeconds())
    );
  }

  // 날짜 포맷 yyyy-MM-dd HH:mm:ss.SSS
  function formatDateTimeWithMs(date: Date) {
    const pad = (n: number, width = 2) => n.toString().padStart(width, "0");
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      " " +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds()) +
      "." +
      pad(date.getMilliseconds(), 3)
    );
  }

  // js_script 데이터 만드는 메서드
  // versions: MSSQL 제품은 배포 서버에 올린 파일명(`${no_issue}|${fg_script}` → no_version)을 그대로 쓴다
  function buildDeployPayload(
    originScripts: any[],
    dataSource1: any[],
    versions?: Record<string, string>
  ) {
    if (!originScripts || originScripts.length === 0) return null;

    const baseTime = new Date();
    const tm_apply = formatDateTimeWithMs(baseTime);

    let seq = 0;
    const updates = originScripts.map((origin) => {
      const no_issue = origin.no_issue;

      // jsonb wrapper 지원
      const originArray = (() => {
        const js = origin.js_script;
        if (!js) return [];
        try {
          if (Array.isArray(js)) return js;
          if (typeof js === "string") return JSON.parse(js || "[]");
          if (typeof js === "object" && "value" in js)
            return JSON.parse(js.value || "[]");
          return [];
        } catch (e) {
          console.error("js_script parse error:", js, e);
          return [];
        }
      })();

      const js_script = originArray.map((scriptObj: any) => {
        const matched = dataSource1.find(
          (row) =>
            row.no_issue === no_issue && row.fg_script === scriptObj.fg_script
        );

        if (matched) {
          const newTime = new Date(baseTime.getTime() + seq * 1000);
          const timestamp = formatDateTime(newTime);
          seq++;

          // MSSQL 은 배포 서버에 올린 파일명(Java 응답), Postgres 는 기존 규칙
          const uploaded = versions?.[`${no_issue}|${matched.fg_script}`];

          return {
            ...scriptObj,
            yn_apply: "Y",
            tm_apply,
            no_version: uploaded ?? `X${timestamp}${matched.fg_script}`,
          };
        } else {
          return scriptObj;
        }
      });

      return {
        cd_firm: userInfo.cd_firm,
        cd_user_amd: userInfo.cd_user,
        no_issue,
        js_script,
      };
    });

    return {
      sfa_issue: {
        update: updates,
      },
    };
  }

  const onClickMenuSyncModal = async () => {
    const result = await showPopupModal({
      title: "메뉴 동기화",
      width: "30%",
      height: "30%",
      okayBtn: true,
      validation: true,
      children: <SN_SFA_ISSUE_REG_004_POP_002 />,
    });

    if (!result || result === "close") return;

    const response = await apiPost("/upgrade/menu-sync", {
      cdMenu: result.menuCode,
      target: result.serverCode,
    });

    if (response.code === 200) {
      alert(`${result.menuCode} 메뉴 연동에 성공하였습니다.`);
    } else {
      alert(`${result.menuCode} 메뉴 연동에 실패하였습니다.`);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* 검색 패널 */}
      <div className="mb-1">
        <FreeForm
          ref={searchRef}
          data={formData}
          controls={formControlData}
          title={"스크립트 관리"}
          header={
            <div>
              <SnButton text={"메뉴 동기화"} onClick={onClickMenuSyncModal} />
            </div>
          }
        />
      </div>

      {/* 본문 */}
      <div className="flex-1 min-h-0 flex flex-row gap-2">
        {/* 좌측 Grid */}
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <div className="w-full h-full flex flex-col">
            <SnGrid
              seq={1}
              ref={gridRef1}
              columnConfig={columns1}
              rowData={dataSource1}
              isEditable={false}
              rowSelection="single"
              onFocusedRowChanged={onFocusedRowChanged}
            />
          </div>
        </SnResizable>

        {/* 우측 Tab */}
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <div className="w-full h-full flex flex-col">
            <SnTab
              titles={["SCRIPT", "FILE"]}
              width={"100%"}
              header={
                <div>
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      onTabBtnsClick("btnReject");
                    }}
                  />
                  <SnButton
                    text={"Script 추출"}
                    onClick={() => {
                      onTabBtnsClick("btnExtract");
                    }}
                  />
                  <SnButton
                    text={"Script 배포"}
                    onClick={() => {
                      onTabBtnsClick("btnDeploy");
                    }}
                  />
                </div>
              }
              onTabChanged={(index) => {
                setActiveTab(index);
              }}
              body={[
                // SCRIPT 탭
                <div className="h-full w-full flex flex-col">
                  <SnMemoBox defaultValue={script} isReadOnly={true} />
                </div>,

                // FILE 탭
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <div style={{ height: "100%", width: "100%" }}>
                    <SnGrid
                      seq={2}
                      ref={gridRef2}
                      columnConfig={columns2}
                      rowData={dataSource2}
                      isEditable={false}
                      rowSelection="single"
                    />
                  </div>
                </div>,
              ]}
            />
          </div>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_SFA_ISSUE_REG_004;
